/**
 * A MatrixFiller is used to fill up a multi-dimensional array (matrix) with a bunch of items. Each item might be configured to span multiple "columns" within the matrix.
 * @param matrix {Array} - An Array of arrays (matrix) that wants to be filled.
 * @param items {Array} - An array of items that will be inserted into the matrix. Each item must respond to the `getColumnSpan()` method.
 * @param props {Object} - Optional values.
 */
export class MatrixFiller {

    constructor(columnCount, items, props = {}) {
        this.columnCount = columnCount;
        this.items = items;

        this.matrix = buildBlankMatrix(this.columnCount, this.items.length);

        // We have several "fillers" related arrays.
        this.fillers = props.hasOwnProperty('fillers') ? props['fillers'] : null;
        this.unusedFillers = this.fillers.slice(); // Make a shallow copy of fillers.
        this.usedFillers = [];

        this.fill();
    }

    fill() {
        this.fillWithItems();
        this.removeEmptyRows();
        this.fillHoles();
    }

    getMatrix() {
        return this.matrix;
    }

    fillWithItems() {
        let currentItemIndex = 0;
        this.matrix.forEach((row) => {
            let itemsForRow = this.getItemsSlice(currentItemIndex);
            currentItemIndex += this.fillRow(row, itemsForRow);
        });
    }

    removeEmptyRows() {
        this.matrix = this.matrix.filter((row) => {
            return !row.every((cell) => typeof cell === 'undefined')
        });
    }

    fillHoles() {
        this.matrix.forEach((row, rowIndex) => {
            this.fillHolesInRow(row, rowIndex);
        });

        // We need to reset our "fillers" related arrays for next time we need to fill holes.
        this.unusedFillers = this.fillers.slice(); // Shallow copy.
        this.usedFillers = [];
    }

    /**
     * Fill a matrix row with fillers whenever the cell in empty.
     * @param row {Array} A row of cells in the matrix.
     * @param rowIndex {number} The index of the row within the matrix.
     */
    fillHolesInRow(row, rowIndex) {
        row.forEach((cell, columnIndex) => {
            const cellsToFillCount = row.filter((cell) => (typeof cell == 'undefined')).length;
            if (typeof cell == 'undefined') {
                const filler = this.getFiller(cellsToFillCount);
                if (filler) this.matrix[rowIndex][columnIndex] = filler;
            }
        });
    }

    /**
     * Fill a predefined row (probably already full of `undefined`) with items. The items may span multiple "columns" in the row.
     * We look over each item to be fitted and check if it will fit into the row. If it does then we insert it into the row before moving to check the next item.
     * If there's not enough space available in the row then we leave a hole (leave the value as it was) for the remainder of the row.
     * On the next iteration the row will be full and we'll return.
     * @param row {Array} - An Array of "spaces" to fill with the passed in items. Most likely it's an array of `undefined`.
     * @param items {Array} - An Array of filler items to insert into the row. Each item must respond to the `getColumnSpan()` method (which returns the number of columns the item spans).
     * @returns {number} - the number of items that were used to fill the row. This may be used for the next call to `fillRow` to appropriately slice the items so each row gets the next set of items.
     */
    fillRow(row, items) {
        let itemsUsed = []; // Keep a record of the items we've used for the return value.
        let rowCursor = 0;
        items.forEach((item) => {
            let emptyCellsCount = row.length - rowCursor;
            if (emptyCellsCount < 1) return itemsUsed.length; // Return early if the row is full.
            let itemSpan = item.getColumnSpan();
            if (emptyCellsCount >= itemSpan) { // if there's enough empty space in the row…
                for (let i = rowCursor; i < itemSpan + rowCursor; i++) {
                    row[i] = item
                } // …insert the item into the row cells.
                rowCursor = rowCursor + itemSpan; // Move the cursor forward the span of the item.
                itemsUsed.push(item); // Record the item as used.
            } else { // …else there's not enough space in the row…
                rowCursor = rowCursor + emptyCellsCount; // …so we leave a hole for the remainder of the row. On the next iteration the row will be full and we'll return early.
            }
        });
        return itemsUsed.length;
    }

    // TODO - use the `cellsToFillCount` parameter to get a filler that spans the number of cells.
    getFiller(cellsToFillCount) {
        let filler = this.unusedFillers.shift();
        if (filler) this.usedFillers.push(filler);
        return filler;
    }

    // Get the remaining items from the passed in index onwards (only for the number of columns though as any more than that would be redundant).
    getItemsSlice(index) {
        let slicedItems = [];
        for (let i = index; i < index + this.columnCount; ++i) {
            if (this.items[i]) {
                slicedItems.push(this.items[i]);
            }
        }
        return slicedItems;
    }

}

function buildBlankMatrix(columnCount, itemCount, fill = undefined) {
    let rowCount = itemCount + 1;
    return Array(rowCount).fill(fill).map(() => Array(columnCount).fill(fill));
}
